import { FC, memo, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material";
import logo from "../../assets/landing/logo.png";
import { mailTo, navLinks } from "../../utils/landing-data";
import { bp, buttonBorder, buttonGradient, font } from "../../styles/landing-tokens";

const Bar = styled("header")({
  position: "absolute",
  top: 38,
  left: 0,
  right: 0,
  zIndex: 20,

  [bp.sm]: {
    top: 20,
  },
});

const Inner = styled("div")({
  width: "100%",
  /** Figma content 1419 wide (x 251→1670) plus the 40px gutter. */
  maxWidth: 1499,
  margin: "0 auto",
  padding: "0 40px",
  display: "flex",
  alignItems: "center",

  [bp.sm]: {
    padding: "0 20px",
  },
});

const Logo = styled("img")({
  width: 201,
  height: 53,
  objectFit: "contain",

  [bp.sm]: {
    width: 130,
    height: 34,
  },
});

/** Figma: the nav starts at x=671, i.e. 219px right of the logo. */
const Nav = styled("nav")({
  display: "flex",
  alignItems: "center",
  gap: 40.2,
  marginLeft: 219,
  marginRight: "auto",

  [bp.lg]: {
    gap: 28,
    marginLeft: 60,
  },
  [bp.md]: {
    display: "none",
  },
});

const navLinkStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 20.1,
  lineHeight: "20.1px",
  letterSpacing: "-0.03em",
  color: "#FFFFFF",
  textDecoration: "none",
  opacity: 0.9,
  transition: "opacity .2s ease",

  "&:hover": { opacity: 1 },
} as const;

const NavLink = styled("button")(navLinkStyle);

/** "Careers" leaves the page, so it needs to be a real anchor. */
const NavAnchor = styled("a")(navLinkStyle);

const HeaderButton = styled("a")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 224,
  height: 46,
  borderRadius: 7.5,
  background: buttonGradient("#7D8188"),
  /** Figma strokes INSIDE — see the note on PrimaryButton. */
  border: "none",
  boxShadow: `inset 0 0 0 1.02px ${buttonBorder}`,
  fontFamily: font.ui,
  fontWeight: 600,
  fontSize: 12.2,
  lineHeight: "13.8px",
  letterSpacing: "0.011em",
  color: "#FFFFFF",
  textDecoration: "none",
  transition: "filter .2s ease",

  "&:hover": { filter: "brightness(1.25)" },

  [bp.md]: { display: "none" },
});

const Burger = styled("button")({
  display: "none",
  background: "none",
  border: "none",
  cursor: "pointer",
  marginLeft: "auto",
  width: 32,
  height: 32,
  padding: 4,
  flexDirection: "column",
  justifyContent: "center",
  gap: 6,

  "& span": {
    display: "block",
    height: 2,
    width: "100%",
    background: "#FFFFFF",
    borderRadius: 2,
    transition: "transform .3s ease, opacity .3s ease",
  },

  [bp.md]: { display: "flex" },
});

const BurgerOpen = styled(Burger)({
  "& span:first-of-type": { transform: "translateY(8px) rotate(45deg)" },
  "& span:nth-of-type(2)": { opacity: 0 },
  "& span:last-of-type": { transform: "translateY(-8px) rotate(-45deg)" },
});

const MobileMenu = styled("div")({
  position: "fixed",
  inset: 0,
  zIndex: 15,
  background: "rgba(6, 11, 20, 0.98)",
  backdropFilter: "blur(20px)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 32,
});

export const LandingHeader: FC = memo(() => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.documentElement.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [isOpen]);

  const scrollTo = useCallback((id: string) => {
    setIsOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const BurgerButton = isOpen ? BurgerOpen : Burger;

  return (
    <>
      <Bar>
        <Inner>
          <Logo src={logo} alt="Evan Szu" />

          <Nav>
            {navLinks.map((l) =>
              l.href ? (
                <NavAnchor key={l.id} href={l.href} target="_blank" rel="noreferrer">
                  {l.name}
                </NavAnchor>
              ) : (
                <NavLink key={l.id} onClick={() => scrollTo(l.id)}>
                  {l.name}
                </NavLink>
              )
            )}
          </Nav>

          <HeaderButton href={mailTo("Let's connect — inquiry from your website")}>
            Get in Touch
          </HeaderButton>

          <BurgerButton aria-label="Menu" onClick={() => setIsOpen((v) => !v)}>
            <span />
            <span />
            <span />
          </BurgerButton>
        </Inner>
      </Bar>

      {isOpen && (
        <MobileMenu>
          {navLinks.map((l) =>
            l.href ? (
              <NavAnchor
                key={l.id}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 26 }}
              >
                {l.name}
              </NavAnchor>
            ) : (
              <NavLink key={l.id} style={{ fontSize: 26 }} onClick={() => scrollTo(l.id)}>
                {l.name}
              </NavLink>
            )
          )}
          <HeaderButton
            href={mailTo("Let's connect — inquiry from your website")}
            sx={{ display: "inline-flex !important", marginTop: "16px" }}
          >
            Get in Touch
          </HeaderButton>
        </MobileMenu>
      )}
    </>
  );
});
